import { Typography, Divider } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { t } from 'ttag';
import { IconButton, Select } from '@coolremote/shared';
import { KeyboardArrowDown } from '@material-ui/icons';
import DeleteIcon from '@icons/DeleteIcon';
import { controlTypes, createOptionsFromEnum } from '../../constants';
import CustomNumericPicker from '../../../BacnetSchedule/AddEditSchedule/CustomNumericPicker';
import { useStyles } from './styles';

/**
 * One row in the Modbus schedule form's Actions section - renders the input
 * matching the action's controlType (list/onOff/toggle select, numeric
 * picker, or a disabled "Unavailable" row for a stale register), plus the
 * delete action shared by all three.
 */
const ScheduleActionRow = ({ object, activePickerId, onValueChange, onNumericClick, onDelete, activePickerRef }) => {
  const classes = useStyles();
  const { controlType, config = {}, value, id, label, disabled } = object;
  const { customEnum, customEnumVisibility, symbol = '' } = config;
  const selectOptions = createOptionsFromEnum(customEnum, customEnumVisibility, value);

  const actionSection = (rowId) => (
    <div className={classes.rightSection}>
      <Divider orientation="vertical" flexItem className={classes.divider} />
      <IconButton className={classes.iconButton} onClick={() => onDelete(rowId)}>
        <DeleteIcon />
      </IconButton>
    </div>
  );

  // No longer controllable (removed, or turned read-only / stopped matching
  // its uiTemplate point since the schedule was saved) - show it so the user
  // knows it's still part of this schedule, but with no editable control,
  // just a way to remove it.
  if (disabled) {
    return (
      <div
        className={clsx(classes.bodyRow, classes.disabledRow)}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography className={classes.selectModeStyle}>{label ?? config?.name ?? id}</Typography>
        <div className={classes.rightSection}>
          <Typography className={classes.selectModeStyle}>{t`Unavailable`}</Typography>
          {actionSection(id)}
        </div>
      </div>
    );
  }

  // Shared select control for list, onOff, toggle
  if (
    controlType === controlTypes.list ||
    controlType === controlTypes.onOff ||
    controlType === controlTypes.toggle
  ) {
    return (
      <div className={classes.bodyRow} onClick={(e) => e.stopPropagation()}>
        <Typography className={classes.selectModeStyle}>{label ?? config?.name}</Typography>
        <div className={classes.rightSection}>
          <Select
            value={value}
            adaptiveWidth
            menuPortal
            onChange={(newValue) => onValueChange(id, newValue)}
            className={classes.selectStyle}
            options={selectOptions}
            arrowIcon={KeyboardArrowDown}
            placeholder="-"
            classes={{
              optionsList: classes.optionsList,
              option: classes.selectOption,
            }}
          />
          {actionSection(id)}
        </div>
      </div>
    );
  }

  // Numeric value control
  if (controlType === controlTypes.numericValue) {
    return (
      <div
        ref={activePickerId === id ? activePickerRef : null}
        className={clsx(
          classes.bodyRow,
          classes.numericRow,
          activePickerId === id && classes.numericRowExpanded
        )}
      >
        <CustomNumericPicker
          pointId={id}
          currentValue={value}
          config={config}
          label={label ?? config?.name}
          handleValueChange={onValueChange}
          activePickerId={activePickerId}
          handleNumericClick={onNumericClick}
          symbol={symbol}
          actionSection={actionSection}
        />
      </div>
    );
  }

  return null;
};

export default ScheduleActionRow;
